import type { BoardQuantity } from "../../../http/deckDesigner/formulas";
import {
  DIFFERENT_BOARD_POSITION_END,
  DIFFERENT_BOARD_POSITION_START,
  RECTANGULAR_SHAPE,
  type DifferentBoardPosition,
} from "../../../http/deckDesigner/types/configurations";
import type { PlaneBounds, PlaneCoordinate } from "./CartesianPlane";
import type { DeckBoardSegment, DifferentBoardKey } from "./DeckBoardsLayer";

export type RectangularDimensions = {
  width: number | null;
  height: number | null;
};

export type LShapeCutoutCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export type LShapeGeometry = {
  bounds: PlaneBounds;
  coordinates: PlaneCoordinate[];
  cutoutCorner: LShapeCutoutCorner;
  innerCorner: PlaneCoordinate;
  secondaryWidth: number;
  secondaryHeight: number;
};

type BoardRegion = PlaneBounds & {
  id: string;
  count: number;
  differentBoardKey?: DifferentBoardKey;
  differentBoardPosition?: DifferentBoardPosition | null;
  differentBoardWidthMeters?: number;
};

type BoardDirection = "horizontal" | "vertical";

// Half a millimetre: the dimension fields take 3 decimals (mm), so two dimensions/coordinates count
// as the same only when they round to the same millimetre. Was 5 mm, which made any change under
// that (e.g. 4.810 -> 4.812) look like "already in sync" - the drawing then kept its old coordinates
// until it was remounted.
export const DIMENSION_TOLERANCE = 0.0005;
export const DEFAULT_L_SHAPE_CUTOUT_CORNER: LShapeCutoutCorner = "top-right";
const LONG_DIFFERENT_BOARD_KEY: DifferentBoardKey = "long";
const SHORT_DIFFERENT_BOARD_KEY: DifferentBoardKey = "short";
// Kept at 5 mm on purpose, separately from DIMENSION_TOLERANCE: whether the boards fill a region is
// a laying tolerance, not the dimensions' precision - tightening it would flag layouts that fit.
const BOARD_FIT_TOLERANCE_METERS = 0.005;
export const METERS_TO_MILLIMETERS = 1000;
export const DEGREES_TO_RADIANS = Math.PI / 180;

export const isPositiveNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

export const isNonNegativeNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

// The value when it's a finite number > 0 (>= 0 for getNonNegativeNumber), the fallback otherwise -
// e.g. a form field that's still empty, or an SVG size given as a string.
export const getPositiveNumber = (value: number | string | null | undefined, fallback: number): number =>
  typeof value === "number" && isPositiveNumber(value) ? value : fallback;

export const getNonNegativeNumber = (value: number | null | undefined, fallback: number): number =>
  isNonNegativeNumber(value) ? value : fallback;

// Millimetre precision, matching the dimension fields' 3 decimals.
const roundDimension = (value: number): number => Math.round(value * 1000) / 1000;

export const coordinatesMatch = (firstCoordinate: PlaneCoordinate, secondCoordinate: PlaneCoordinate): boolean =>
  firstCoordinate.x === secondCoordinate.x && firstCoordinate.y === secondCoordinate.y;

export const getSegmentLength = (start: PlaneCoordinate, end: PlaneCoordinate): number =>
  Math.hypot(end.x - start.x, end.y - start.y);

// Whether going from start to end runs at least as much along x as along y - an exactly axis-aligned
// edge, or the dominant axis of a pointer drag.
export const isMostlyHorizontal = (start: PlaneCoordinate, end: PlaneCoordinate): boolean =>
  Math.abs(end.x - start.x) >= Math.abs(end.y - start.y);

const getSortedValues = (values: number[]): number[] =>
  Array.from(new Set(values)).sort((firstValue, secondValue) => firstValue - secondValue);

const isAxisAlignedPath = (coordinates: PlaneCoordinate[]): boolean =>
  coordinates.every((coordinate, index) => {
    const nextCoordinate = coordinates[(index + 1) % coordinates.length];

    if (!nextCoordinate) {
      return false;
    }

    const sharesX = coordinate.x === nextCoordinate.x;
    const sharesY = coordinate.y === nextCoordinate.y;

    return sharesX !== sharesY;
  });

const hasCoordinate = (coordinates: PlaneCoordinate[], nextCoordinate: PlaneCoordinate): boolean =>
  coordinates.some((coordinate) => coordinatesMatch(coordinate, nextCoordinate));

export const getCoordinateBounds = (coordinates: PlaneCoordinate[]): PlaneBounds | null => {
  if (coordinates.length < 2) {
    return null;
  }

  return coordinates.reduce<PlaneBounds>(
    (bounds, coordinate) => ({
      minX: Math.min(bounds.minX, coordinate.x),
      maxX: Math.max(bounds.maxX, coordinate.x),
      minY: Math.min(bounds.minY, coordinate.y),
      maxY: Math.max(bounds.maxY, coordinate.y),
    }),
    {
      minX: coordinates[0]?.x ?? 0,
      maxX: coordinates[0]?.x ?? 0,
      minY: coordinates[0]?.y ?? 0,
      maxY: coordinates[0]?.y ?? 0,
    },
  );
};

export const getRectangularCoordinatesFromBounds = (bounds: PlaneBounds): PlaneCoordinate[] => [
  { x: bounds.minX, y: bounds.maxY },
  { x: bounds.maxX, y: bounds.maxY },
  { x: bounds.maxX, y: bounds.minY },
  { x: bounds.minX, y: bounds.minY },
];

export const finishRectangleCoordinates = (coordinates: PlaneCoordinate[]): PlaneCoordinate[] | null => {
  if (coordinates.length !== 4 || !isAxisAlignedPath(coordinates)) {
    return null;
  }

  const xValues = getSortedValues(coordinates.map((coordinate) => coordinate.x));
  const yValues = getSortedValues(coordinates.map((coordinate) => coordinate.y));

  if (xValues.length !== 2 || yValues.length !== 2) {
    return null;
  }

  const minX = xValues[0];
  const maxX = xValues[1];
  const minY = yValues[0];
  const maxY = yValues[1];

  if (minX == null || maxX == null || minY == null || maxY == null) {
    return null;
  }

  return getRectangularCoordinatesFromBounds({ minX, maxX, minY, maxY });
};

export const isCutoutOnRight = (cutoutCorner: LShapeCutoutCorner): boolean =>
  cutoutCorner === "top-right" || cutoutCorner === "bottom-right";

export const isCutoutOnTop = (cutoutCorner: LShapeCutoutCorner): boolean =>
  cutoutCorner === "top-left" || cutoutCorner === "top-right";

export const getLShapeCoordinatesFromBounds = (
  bounds: PlaneBounds,
  innerCorner: PlaneCoordinate,
  cutoutCorner: LShapeCutoutCorner,
): PlaneCoordinate[] => {
  if (cutoutCorner === "top-left") {
    return [
      { x: innerCorner.x, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.minX, y: bounds.minY },
      { x: bounds.minX, y: innerCorner.y },
      innerCorner,
    ];
  }

  if (cutoutCorner === "bottom-left") {
    return [
      { x: bounds.minX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.minY },
      { x: innerCorner.x, y: bounds.minY },
      innerCorner,
      { x: bounds.minX, y: innerCorner.y },
    ];
  }

  if (cutoutCorner === "bottom-right") {
    return [
      { x: bounds.minX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.maxX, y: innerCorner.y },
      innerCorner,
      { x: innerCorner.x, y: bounds.minY },
      { x: bounds.minX, y: bounds.minY },
    ];
  }

  return [
    { x: bounds.minX, y: bounds.maxY },
    { x: innerCorner.x, y: bounds.maxY },
    innerCorner,
    { x: bounds.maxX, y: innerCorner.y },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.minX, y: bounds.minY },
  ];
};

const getSecondaryWidth = (
  bounds: PlaneBounds,
  innerCorner: PlaneCoordinate,
  cutoutCorner: LShapeCutoutCorner,
): number => (isCutoutOnRight(cutoutCorner) ? bounds.maxX - innerCorner.x : innerCorner.x - bounds.minX);

const getSecondaryHeight = (
  bounds: PlaneBounds,
  innerCorner: PlaneCoordinate,
  cutoutCorner: LShapeCutoutCorner,
): number => (isCutoutOnTop(cutoutCorner) ? bounds.maxY - innerCorner.y : innerCorner.y - bounds.minY);

export const getLShapeGeometry = (coordinates: PlaneCoordinate[]): LShapeGeometry | null => {
  if (coordinates.length !== 6 || !isAxisAlignedPath(coordinates)) {
    return null;
  }

  const xValues = getSortedValues(coordinates.map((coordinate) => coordinate.x));
  const yValues = getSortedValues(coordinates.map((coordinate) => coordinate.y));

  if (xValues.length !== 3 || yValues.length !== 3) {
    return null;
  }

  const minX = xValues[0];
  const innerX = xValues[1];
  const maxX = xValues[2];
  const minY = yValues[0];
  const innerY = yValues[1];
  const maxY = yValues[2];

  if (minX == null || innerX == null || maxX == null || minY == null || innerY == null || maxY == null) {
    return null;
  }

  const bounds = { minX, maxX, minY, maxY };
  const innerCorner = { x: innerX, y: innerY };
  const cutoutCorners: LShapeCutoutCorner[] = ["top-left", "top-right", "bottom-left", "bottom-right"];

  for (const cutoutCorner of cutoutCorners) {
    const coordinatesForCorner = getLShapeCoordinatesFromBounds(bounds, innerCorner, cutoutCorner);

    if (coordinatesForCorner.every((coordinate) => hasCoordinate(coordinates, coordinate))) {
      return {
        bounds,
        coordinates: coordinatesForCorner,
        cutoutCorner,
        innerCorner,
        secondaryWidth: getSecondaryWidth(bounds, innerCorner, cutoutCorner),
        secondaryHeight: getSecondaryHeight(bounds, innerCorner, cutoutCorner),
      };
    }
  }

  return null;
};

export const finishLShapeCoordinates = (coordinates: PlaneCoordinate[]): PlaneCoordinate[] | null =>
  getLShapeGeometry(coordinates)?.coordinates ?? null;

export const getRectangularDimensions = (coordinates: PlaneCoordinate[]): RectangularDimensions => {
  const bounds = getCoordinateBounds(coordinates);

  if (!bounds) {
    return {
      width: null,
      height: null,
    };
  }

  return {
    width: roundDimension(bounds.maxX - bounds.minX),
    height: roundDimension(bounds.maxY - bounds.minY),
  };
};

export type LShapeDimensions = {
  width: number | null;
  height: number | null;
  secondaryWidth: number | null;
  secondaryHeight: number | null;
};

export const getLShapeDimensions = (coordinates: PlaneCoordinate[]): LShapeDimensions => {
  const geometry = getLShapeGeometry(coordinates);
  const bounds = geometry?.bounds ?? getCoordinateBounds(coordinates);

  if (!bounds) {
    return {
      width: null,
      height: null,
      secondaryWidth: null,
      secondaryHeight: null,
    };
  }

  const width = roundDimension(bounds.maxX - bounds.minX);
  const height = roundDimension(bounds.maxY - bounds.minY);

  if (!geometry) {
    return {
      width,
      height,
      secondaryWidth: null,
      secondaryHeight: null,
    };
  }

  return {
    width,
    height,
    secondaryWidth: roundDimension(geometry.secondaryWidth),
    secondaryHeight: roundDimension(geometry.secondaryHeight),
  };
};

export const getRectangularCoordinates = (base: number | null, height: number | null): PlaneCoordinate[] => {
  if (!isPositiveNumber(base) || !isPositiveNumber(height)) {
    return [];
  }

  return getRectangularCoordinatesFromBounds({
    minX: -base / 2,
    maxX: base / 2,
    minY: -height / 2,
    maxY: height / 2,
  });
};

export const getLShapeCoordinates = (
  base: number | null,
  height: number | null,
  secondaryBase: number | null,
  secondaryHeight: number | null,
  cutoutCorner: LShapeCutoutCorner,
): PlaneCoordinate[] => {
  if (!isPositiveNumber(base) || !isPositiveNumber(height)) {
    return [];
  }

  const bounds = {
    minX: -base / 2,
    maxX: base / 2,
    minY: -height / 2,
    maxY: height / 2,
  };

  if (!isPositiveNumber(secondaryBase) || !isPositiveNumber(secondaryHeight)) {
    return [];
  }

  const innerCorner = {
    x: isCutoutOnRight(cutoutCorner) ? bounds.maxX - secondaryBase : bounds.minX + secondaryBase,
    y: isCutoutOnTop(cutoutCorner) ? bounds.maxY - secondaryHeight : bounds.minY + secondaryHeight,
  };

  return innerCorner.x > bounds.minX &&
    innerCorner.x < bounds.maxX &&
    innerCorner.y > bounds.minY &&
    innerCorner.y < bounds.maxY
    ? getLShapeCoordinatesFromBounds(bounds, innerCorner, cutoutCorner)
    : getRectangularCoordinatesFromBounds(bounds);
};

export const areCoordinatesEqual = (first: PlaneCoordinate[], second: PlaneCoordinate[]): boolean =>
  first.length === second.length &&
  first.every((coordinate, index) => {
    const nextCoordinate = second[index];

    return (
      !!nextCoordinate &&
      Math.abs(coordinate.x - nextCoordinate.x) <= DIMENSION_TOLERANCE &&
      Math.abs(coordinate.y - nextCoordinate.y) <= DIMENSION_TOLERANCE
    );
  });

export const hasMatchingDimension = (firstValue: number | null, secondValue: number | null): boolean =>
  firstValue != null && secondValue != null && Math.abs(firstValue - secondValue) <= DIMENSION_TOLERANCE;

// Standard ray-casting point-in-polygon test - used to tell which side of a shape edge is filled
// vs empty (e.g. to push a dimension label away from the shape), since that can't be inferred from
// the overall bounding-box center alone on a concave edge like an L-shape's cutout side.
export const isPointInPolygon = (point: PlaneCoordinate, polygon: PlaneCoordinate[]): boolean => {
  let isInside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const current = polygon[i];
    const previous = polygon[j];

    if (!current || !previous) {
      continue;
    }

    const crossesRay = current.y > point.y !== previous.y > point.y;
    const intersectsX =
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;

    if (crossesRay && intersectsX) {
      isInside = !isInside;
    }
  }

  return isInside;
};

export const coordinatesMatchFormDimensions = (
  coordinates: PlaneCoordinate[],
  selectedShape: string,
  base: number | null,
  height: number | null,
  secondaryBase: number | null,
  secondaryHeight: number | null,
): boolean => {
  if (selectedShape === RECTANGULAR_SHAPE) {
    const dimensions = getRectangularDimensions(coordinates);

    return (
      coordinates.length === 4 &&
      hasMatchingDimension(dimensions.width, base) &&
      hasMatchingDimension(dimensions.height, height)
    );
  }

  const dimensions = getLShapeDimensions(coordinates);
  const hasMatchingOuterDimensions =
    hasMatchingDimension(dimensions.width, base) && hasMatchingDimension(dimensions.height, height);

  return (
    coordinates.length === 6 &&
    isPositiveNumber(secondaryBase) &&
    isPositiveNumber(secondaryHeight) &&
    hasMatchingOuterDimensions &&
    hasMatchingDimension(dimensions.secondaryWidth, secondaryBase) &&
    hasMatchingDimension(dimensions.secondaryHeight, secondaryHeight)
  );
};

const createBoardSegments = (
  regions: BoardRegion[],
  boardWidthMeters: number,
  gapMeters: number,
  direction: BoardDirection,
): DeckBoardSegment[] =>
  regions.flatMap((region) => {
    const isHorizontal = direction === "horizontal";
    const availableSpace = isHorizontal ? region.maxY - region.minY : region.maxX - region.minX;
    const maxGap = region.count > 1 ? availableSpace / (region.count - 1) : 0;
    const gap = region.count > 1 ? Math.min(gapMeters, maxGap) : 0;
    const availableBoardSpace = Math.max(availableSpace - Math.max(region.count - 1, 0) * gap, 0);
    const specialBoardFits =
      region.differentBoardWidthMeters != null &&
      region.differentBoardWidthMeters >= boardWidthMeters &&
      region.differentBoardWidthMeters <= boardWidthMeters * 2 &&
      Math.abs(
        availableSpace -
          (Math.max(region.count - 1, 0) * boardWidthMeters +
            region.differentBoardWidthMeters +
            Math.max(region.count - 1, 0) * gap),
      ) <= BOARD_FIT_TOLERANCE_METERS;
    const specialBoardPosition = region.differentBoardPosition ?? DIFFERENT_BOARD_POSITION_END;
    const specialBoardWidthMeters = specialBoardFits ? region.differentBoardWidthMeters : undefined;
    const boardSizes =
      specialBoardWidthMeters != null
        ? specialBoardPosition === DIFFERENT_BOARD_POSITION_START
          ? [specialBoardWidthMeters, ...Array.from({ length: Math.max(region.count - 1, 0) }, () => boardWidthMeters)]
          : [...Array.from({ length: Math.max(region.count - 1, 0) }, () => boardWidthMeters), specialBoardWidthMeters]
        : Array.from({ length: region.count }, () => Math.min(boardWidthMeters, availableBoardSpace / region.count));
    const usedSpace = boardSizes.reduce((total, boardSize) => total + boardSize, Math.max(region.count - 1, 0) * gap);
    const inset = Math.max((availableSpace - usedSpace) / 2, 0);
    let offset = inset;

    return boardSizes.map((boardSize, index) => {
      const isDifferentWidth =
        specialBoardFits &&
        region.differentBoardKey != null &&
        (specialBoardPosition === DIFFERENT_BOARD_POSITION_START ? index === 0 : index === boardSizes.length - 1);
      const differentBoardMetadata = isDifferentWidth
        ? {
            differentBoardDirection: direction,
            differentBoardKey: region.differentBoardKey,
            differentBoardPosition: specialBoardPosition,
            differentBoardRegion: {
              minX: region.minX,
              maxX: region.maxX,
              minY: region.minY,
              maxY: region.maxY,
            },
          }
        : {};

      if (isHorizontal) {
        const top = region.maxY - offset;
        const bottom = Math.max(top - boardSize, region.minY);
        offset += boardSize + (index < boardSizes.length - 1 ? gap : 0);

        return {
          id: isDifferentWidth ? `${region.id}-different` : `${region.id}-${index}`,
          isDifferentWidth,
          ...differentBoardMetadata,
          corners: [
            { x: region.minX, y: top },
            { x: region.maxX, y: top },
            { x: region.maxX, y: bottom },
            { x: region.minX, y: bottom },
          ],
        };
      }

      const left = region.minX + offset;
      const right = Math.min(left + boardSize, region.maxX);
      offset += boardSize + (index < boardSizes.length - 1 ? gap : 0);

      return {
        id: isDifferentWidth ? `${region.id}-different` : `${region.id}-${index}`,
        isDifferentWidth,
        ...differentBoardMetadata,
        corners: [
          { x: left, y: region.maxY },
          { x: right, y: region.maxY },
          { x: right, y: region.minY },
          { x: left, y: region.minY },
        ],
      };
    });
  });

export const getBoardSegments = (
  coordinates: PlaneCoordinate[],
  selectedShape: string,
  boardWidth: number | null,
  gap: number | null,
  hasHorizontalBoards: boolean | null,
  boardQuantity: BoardQuantity | null,
  longDifferentBoardWidth: number | null = null,
  shortDifferentBoardWidth: number | null = null,
  longDifferentBoardPosition: DifferentBoardPosition | null = DIFFERENT_BOARD_POSITION_END,
  shortDifferentBoardPosition: DifferentBoardPosition | null = DIFFERENT_BOARD_POSITION_END,
): DeckBoardSegment[] => {
  if (!isPositiveNumber(boardWidth) || !isNonNegativeNumber(gap) || !boardQuantity) {
    return [];
  }

  const bounds = getCoordinateBounds(coordinates);

  if (!bounds) {
    return [];
  }

  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const boardWidthMeters = boardWidth / 1000;
  const longDifferentBoardWidthMeters =
    isPositiveNumber(longDifferentBoardWidth) &&
    longDifferentBoardWidth >= boardWidth &&
    longDifferentBoardWidth <= boardWidth * 2
      ? longDifferentBoardWidth / 1000
      : undefined;
  const shortDifferentBoardWidthMeters =
    isPositiveNumber(shortDifferentBoardWidth) &&
    shortDifferentBoardWidth >= boardWidth &&
    shortDifferentBoardWidth <= boardWidth * 2
      ? shortDifferentBoardWidth / 1000
      : undefined;
  const gapMeters = gap / 1000;
  const isHorizontal = hasHorizontalBoards !== false;
  const direction: BoardDirection = isHorizontal ? "horizontal" : "vertical";

  if (width <= 0 || height <= 0) {
    return [];
  }

  if (selectedShape === RECTANGULAR_SHAPE) {
    if (coordinates.length !== 4) {
      return [];
    }

    return createBoardSegments(
      [
        {
          ...bounds,
          id: "rectangular",
          count: boardQuantity.total,
          differentBoardKey: LONG_DIFFERENT_BOARD_KEY,
          differentBoardPosition: longDifferentBoardPosition,
          differentBoardWidthMeters: longDifferentBoardWidthMeters,
        },
      ],
      boardWidthMeters,
      gapMeters,
      direction,
    );
  }

  const geometry = getLShapeGeometry(coordinates);

  if (!geometry) {
    return [];
  }

  const { cutoutCorner, innerCorner } = geometry;
  const cutoutOnRight = isCutoutOnRight(cutoutCorner);
  const cutoutOnTop = isCutoutOnTop(cutoutCorner);
  const secondaryBase = cutoutOnRight ? bounds.maxX - innerCorner.x : innerCorner.x - bounds.minX;
  const secondaryHeight = cutoutOnTop ? bounds.maxY - innerCorner.y : innerCorner.y - bounds.minY;

  if (secondaryBase <= 0 || secondaryHeight <= 0 || secondaryBase >= width || secondaryHeight >= height) {
    return [];
  }

  const regions: BoardRegion[] = (
    isHorizontal
      ? [
          {
            id: "l-full",
            minX: bounds.minX,
            maxX: bounds.maxX,
            minY: cutoutOnTop ? bounds.minY : innerCorner.y,
            maxY: cutoutOnTop ? innerCorner.y : bounds.maxY,
            count: boardQuantity.long,
            differentBoardKey: LONG_DIFFERENT_BOARD_KEY,
            differentBoardPosition: longDifferentBoardPosition,
            differentBoardWidthMeters: longDifferentBoardWidthMeters,
          },
          {
            id: "l-short",
            minX: cutoutOnRight ? bounds.minX : innerCorner.x,
            maxX: cutoutOnRight ? innerCorner.x : bounds.maxX,
            minY: cutoutOnTop ? innerCorner.y : bounds.minY,
            maxY: cutoutOnTop ? bounds.maxY : innerCorner.y,
            count: boardQuantity.short,
            differentBoardKey: SHORT_DIFFERENT_BOARD_KEY,
            differentBoardPosition: shortDifferentBoardPosition,
            differentBoardWidthMeters: shortDifferentBoardWidthMeters,
          },
        ]
      : [
          {
            id: "l-full",
            minX: cutoutOnRight ? bounds.minX : innerCorner.x,
            maxX: cutoutOnRight ? innerCorner.x : bounds.maxX,
            minY: bounds.minY,
            maxY: bounds.maxY,
            count: boardQuantity.long,
            differentBoardKey: LONG_DIFFERENT_BOARD_KEY,
            differentBoardPosition: longDifferentBoardPosition,
            differentBoardWidthMeters: longDifferentBoardWidthMeters,
          },
          {
            id: "l-short",
            minX: cutoutOnRight ? innerCorner.x : bounds.minX,
            maxX: cutoutOnRight ? bounds.maxX : innerCorner.x,
            minY: cutoutOnTop ? bounds.minY : innerCorner.y,
            maxY: cutoutOnTop ? innerCorner.y : bounds.maxY,
            count: boardQuantity.short,
            differentBoardKey: SHORT_DIFFERENT_BOARD_KEY,
            differentBoardPosition: shortDifferentBoardPosition,
            differentBoardWidthMeters: shortDifferentBoardWidthMeters,
          },
        ]
  ).filter((region) => region.count > 0);

  return createBoardSegments(regions, boardWidthMeters, gapMeters, direction);
};
